import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';
import ContactEditModal from '../components/contacts/ContactEditModal';
import Pagination from '../components/common/Pagination';

const { FiSearch, FiPlus, FiMail, FiPhone, FiBuilding } = FiIcons;

function Contacts() {
  const { data, updateContact, deleteContact, addContact } = useLexware();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  
  // Pagination State for Contacts
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const filteredContacts = useMemo(() => {
    const contacts = data.contacts || [];
    return contacts.filter(contact => 
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data.contacts, searchTerm]);

  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredContacts.slice(start, start + pageSize);
  }, [filteredContacts, currentPage, pageSize]);

  const handleSave = async (updated) => {
    if (isAdding) {
      await addContact(updated);
    } else {
      await updateContact(updated);
    }
    setSelectedContact(null);
    setIsAdding(false);
  };

  const handleDelete = async (id) => {
    await deleteContact(id);
    setSelectedContact(null);
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kontakte</h1>
          <p className="text-sm text-gray-500 font-medium">Insgesamt {data.contacts?.length || 0} Ansprechpartner im System</p>
        </div>
        <button onClick={() => { setIsAdding(true); setSelectedContact({ name: '', email: '', phone: '', company: '', role: 'Neu' }); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200">
          <SafeIcon icon={FiPlus} className="text-sm" /> Neuer Kontakt
        </button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Kontakte durchsuchen..." 
            value={searchTerm} 
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} 
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none" 
          />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedContacts.map((contact, index) => (
          <motion.div 
            key={contact.id} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: index * 0.05 }} 
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all group flex flex-col justify-between"
          >
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100">
                  <span className="text-blue-600 font-black text-lg">
                    {contact.name?.charAt(0) || '?'}
                  </span>
                </div>
                <span className="text-[10px] font-black px-2 py-1 bg-gray-100 text-gray-500 rounded-lg uppercase tracking-wider">
                  {contact.role || 'Kontakt'}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-black text-gray-900 text-lg leading-tight group-hover:text-blue-600 transition-colors">{contact.name}</h3>
                  <div className="flex items-center gap-2 text-xs font-bold text-gray-400 mt-1 uppercase">
                    <SafeIcon icon={FiBuilding} className="text-blue-400" /> {contact.company || 'Keine Firma'}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100">
                    <SafeIcon icon={FiMail} className="text-gray-400" /> <span className="truncate">{contact.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100">
                    <SafeIcon icon={FiPhone} className="text-gray-400" /> {contact.phone || '-'}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-6 mt-6 border-t border-gray-50">
              <button onClick={() => { setIsAdding(false); setSelectedContact(contact); }} className="flex-1 py-2.5 text-sm font-bold bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors">
                Bearbeiten
              </button>
              <button onClick={() => window.location.href = `mailto:${contact.email}`} className="w-12 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 flex items-center justify-center transition-all">
                <SafeIcon icon={FiMail} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Pagination Integration for Contacts Grid */}
      <Pagination 
        currentPage={currentPage}
        totalItems={filteredContacts.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        className="rounded-xl border border-gray-200 mt-4" 
      />

      <AnimatePresence>
        {selectedContact && (
          <ContactEditModal 
            contact={selectedContact} 
            companyName={selectedContact.company}
            onClose={() => { setSelectedContact(null); setIsAdding(false); }} 
            onSave={handleSave}
            onDelete={handleDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Contacts;